import { useState } from 'react';
import { AirParticles } from '@/components/Layout';
import { TrendingUp, Users, Building2, Globe, ArrowRight, Download, Mail } from 'lucide-react';
import logger from '@/utils/logger';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Investor() {
  const [form, setForm] = useState({ name: '', email: '', firm: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/investor-inquiry`, form);
      setSubmitted(true);
    } catch (err) {
      logger.error('Investor inquiry failed:', err.message);
    }
    setLoading(false);
  };

  return (
    <div>
      <section className="relative pt-32 pb-20 px-6 lg:px-12 overflow-hidden" style={{ background: 'var(--soft-white)' }} data-testid="investor-hero">
        <AirParticles count={10} color="rgba(15,76,92,0.04)" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="overline-teal mb-4 animate-fade-up">For Investors</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6 animate-fade-up delay-100" style={{ fontFamily: 'Inter' }} data-testid="investor-title">
            Invest in the air<br />we <span style={{ color: 'var(--aqua)' }}>breathe</span>.
          </h1>
          <p className="text-base lg:text-lg leading-relaxed max-w-2xl mx-auto animate-fade-up delay-200" style={{ color: 'var(--text-secondary)' }}>
            The indoor air quality market is projected to reach $15.8B by 2028.
            Jagdale Air is building the infrastructure layer for smart buildings.
          </p>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-20 px-6 lg:px-12 text-center" style={{ background: 'var(--bg-surface, #0A2A32)' }} data-testid="market-opportunity">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--aqua)' }}>Market Opportunity</p>
          <p className="text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-tighter mb-4" style={{ color: 'var(--aqua)', fontFamily: 'Inter' }}>$100B+</p>
          <p className="text-xl md:text-2xl font-medium mb-4" style={{ fontFamily: 'Inter', color: 'var(--text-light, #E6F4F7)' }}>Global indoor air quality market</p>
          <p className="text-sm max-w-lg mx-auto" style={{ color: 'var(--text-secondary-light, #9FBCC3)' }}>Buildings are the last undigitized infrastructure layer. Vayu is building the operating system for air in buildings — hardware, software, and data.</p>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="section-padding px-6 lg:px-12" data-testid="investor-metrics">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {[
              { icon: TrendingUp, num: '$15.8B', label: 'Market Size by 2028', color: 'var(--deep-teal)' },
              { icon: Building2, num: '2 LOI', label: 'From Builders for Smart Homes', color: '#8B5CF6' },
              { icon: Globe, num: '8', label: 'Cities Active', color: 'var(--green-good)' },
            ].map((m) => (
              <div key={m.label} className="card-hover p-6 text-center animate-fade-up" data-testid={`metric-${m.label}`}>
                <m.icon className="w-6 h-6 mx-auto mb-3" style={{ color: m.color }} strokeWidth={1.5} />
                <p className="stat-number text-2xl md:text-3xl mb-1" style={{ color: m.color }}>{m.num}</p>
                <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{m.label}</p>
              </div>
            ))}
          </div>

          {/* Vision Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card-hover p-8" data-testid="vision-card-1">
              <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Inter' }}>The Opportunity</h3>
              <div className="space-y-3 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                <p>90% of our time is spent indoors, yet indoor air is 2-5x more polluted than outdoor air. India's rapid urbanization is making this worse.</p>
                <p>Current solutions (purifiers) treat symptoms. Vayu treats the root cause — the building envelope itself. This is infrastructure, not appliances.</p>
              </div>
            </div>
            <div className="card-hover p-8" data-testid="vision-card-2">
              <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Inter' }}>Our Moat</h3>
              <div className="space-y-3 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                <p>Hardware + Software + Data. Vayu is the only system that combines window-level filtration with AI-powered air management and a network effect.</p>
                <p>Each installation makes our system smarter. Builder partnerships create B2B lock-in. Data from 8 cities powers our predictive engine.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pitch Deck + Contact */}
      <section className="section-padding px-6 lg:px-12 bg-teal-gradient relative overflow-hidden" data-testid="investor-contact">
        <AirParticles count={10} color="rgba(63,167,214,0.08)" />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6" style={{ fontFamily: 'Inter' }}>Let's talk.</h2>
              <p className="text-base text-white/50 mb-8">Interested in learning more about Jagdale Air? Download our pitch deck or reach out directly.</p>
              <a href="/jagdale-air-pitch-deck.pdf" target="_blank" rel="noopener noreferrer" className="btn-aqua inline-flex items-center gap-2 mb-6" data-testid="download-deck-btn">
                <Download className="w-4 h-4" /> Download Pitch Deck
              </a>
              <div className="mt-8 flex items-center gap-3">
                <Mail className="w-4 h-4 text-white/40" />
                <span className="text-sm text-white/50">info@jagdaleair.com</span>
              </div>
            </div>

            <div className="rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="investor-form-card">
              {submitted ? (
                <div className="text-center py-8" data-testid="investor-success">
                  <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Inter' }}>Thank you</h3>
                  <p className="text-sm text-white/50">Our team will reach out shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="investor-form">
                  <input data-testid="investor-name" type="text" required placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm bg-transparent text-white placeholder-white/30" style={{ border: '1px solid rgba(255,255,255,0.1)' }} />
                  <input data-testid="investor-email" type="email" required placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm bg-transparent text-white placeholder-white/30" style={{ border: '1px solid rgba(255,255,255,0.1)' }} />
                  <input data-testid="investor-firm" type="text" placeholder="Firm / Fund (optional)" value={form.firm} onChange={e => setForm({...form, firm: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm bg-transparent text-white placeholder-white/30" style={{ border: '1px solid rgba(255,255,255,0.1)' }} />
                  <textarea data-testid="investor-message" required rows={3} placeholder="Your message..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm bg-transparent text-white placeholder-white/30 resize-none" style={{ border: '1px solid rgba(255,255,255,0.1)' }} />
                  <button type="submit" disabled={loading} className="btn-aqua w-full" data-testid="investor-submit" style={{ opacity: loading ? 0.7 : 1 }}>{loading ? 'Sending...' : 'Send Message'}</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
